import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, ArrowRight, Clock, FileCheck, BarChart3, Shield, Globe, Settings, Eye, FileText, CircleDot, XCircle, MinusCircle, PlusCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { seoContent, content } from '../data/caseStudyCyberSecurityData';

const CaseStudyCyberSecurity = () => {
  const { language: lang } = useLanguage();

  const seo = seoContent[lang] || seoContent.de;
  const t = content[lang];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#E7F0FA] to-white pt-20">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        path="/case-study/cyber-security"
        lang={lang}
      />
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-[#C8D8E8]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link 
            to="/portfolio" 
            className="inline-flex items-center gap-2 text-[#2E5E99] hover:text-[#1E4F85] transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.backLink}
          </Link>
          <div className="flex items-center gap-3 mb-2">
            <span className="px-3 py-1 bg-[#2E5E99] text-white text-sm font-medium rounded-full">
              {t.title}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0D2440]">
            {t.subtitle}
          </h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Hero Image */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="/assets/instruction-system.jpeg" 
              alt={t.subtitle}
              className="w-full h-[400px] object-cover object-[75%_30%]"
            />
          </div>
        </motion.section>

        {/* Ausgangssituation */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.situation.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.situation.text}</p>
            <p className="text-[#0D2440] font-medium mb-3">{t.situation.subtitle}</p>
            <ul className="space-y-2">
              {t.situation.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CircleDot className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Herausforderung */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.challenge.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.challenge.text}</p>
            <ul className="space-y-2 mb-4">
              {t.challenge.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] italic">{t.challenge.extra}</p>
          </div>
        </motion.section>

        {/* Lösung */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-br from-[#E7F0FA] to-white rounded-xl p-6 md:p-8 border border-[#2E5E99]/30 shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#2E5E99]/10 rounded-lg flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#2E5E99]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.solution.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.solution.text}</p>
            <ul className="space-y-2">
              {t.solution.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-[#2E5E99] mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Integration */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.integration.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.integration.text}</p>
            <ul className="space-y-2 mb-6">
              {t.integration.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-purple-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            
            <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
              <h4 className="font-semibold text-[#0D2440] mb-2">{t.integration.groupConnector.title}</h4>
              <p className="text-[#5A7A9B] text-sm mb-3">{t.integration.groupConnector.text}</p>
              <ul className="space-y-1">
                {t.integration.groupConnector.points.map((point) => (
                  <li key={point} className="flex items-start gap-2 text-[#5A7A9B] text-sm">
                    <CheckCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* Training Concept */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-cyan-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.trainingConcept.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-6">{t.trainingConcept.text}</p>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Year 1 */}
              <div className="bg-[#E7F0FA] rounded-lg p-4">
                <h4 className="font-semibold text-[#0D2440] mb-2">{t.trainingConcept.year1.title}</h4>
                <p className="text-[#5A7A9B] text-sm mb-3">{t.trainingConcept.year1.text}</p>
                <ul className="space-y-1">
                  {t.trainingConcept.year1.points.map((point) => (
                    <li key={point} className="flex items-start gap-2 text-[#5A7A9B] text-sm">
                      <span className="text-[#2E5E99] mt-1">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Year 2 */}
              <div className="bg-cyan-50 rounded-lg p-4">
                <h4 className="font-semibold text-[#0D2440] mb-2">{t.trainingConcept.year2.title}</h4>
                <p className="text-[#5A7A9B] text-sm">{t.trainingConcept.year2.text}</p>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg p-4 border border-green-200">
              <h4 className="font-semibold text-green-700 mb-2">{t.trainingConcept.automatic.title}</h4>
              <p className="text-[#5A7A9B] text-sm">{t.trainingConcept.automatic.text}</p>
            </div>
          </div>
        </motion.section>

        {/* International */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Globe className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.international.title}</h3>
            </div>
            <p className="text-[#5A7A9B]">{t.international.text}</p>
          </div>
        </motion.section>

        {/* Transparency */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.transparency.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.transparency.text}</p>
            <ul className="space-y-2 mb-4">
              {t.transparency.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-indigo-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] italic">{t.transparency.conclusion}</p>
          </div>
        </motion.section>

        {/* Reporting */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.reporting.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.reporting.text}</p>
            <ul className="space-y-2 mb-4">
              {t.reporting.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-orange-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
            <p className="text-[#5A7A9B] font-medium">{t.reporting.conclusion}</p>
          </div>
        </motion.section>

        {/* Ergebnis */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mb-12"
        >
          <div className="bg-white rounded-xl p-6 md:p-8 border border-[#C8D8E8] shadow-lg">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-[#0D2440]">{t.result.title}</h3>
            </div>
            <p className="text-[#5A7A9B] mb-4">{t.result.text}</p>
            <ul className="space-y-2">
              {t.result.points.map((point) => (
                <li key={point} className="flex items-start gap-2 text-[#5A7A9B]">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  {point}
                </li>
              ))}
            </ul>
          </div>
        </motion.section>

        {/* Vorher/Nachher Vergleich */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="mb-16"
        >
          <h3 className="text-2xl font-bold text-[#0D2440] text-center mb-8">{t.comparison.title}</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Vorher */}
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h4 className="text-xl font-semibold text-red-700 mb-4">{t.comparison.before.title}</h4>
              <ul className="space-y-3">
                {t.comparison.before.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-[#5A7A9B]">
                    <MinusCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Nachher */}
            <div className="bg-green-50 rounded-xl p-6 border border-green-200">
              <h4 className="text-xl font-semibold text-green-700 mb-4">{t.comparison.after.title}</h4>
              <ul className="space-y-3">
                {t.comparison.after.points.map((point) => (
                  <li key={point} className="flex items-center gap-2 text-[#5A7A9B]">
                    <PlusCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
        >
          <div className="bg-gradient-to-r from-[#2E5E99] to-[#1E4F85] rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-4">
              {t.cta.title}
            </h2>
            <p className="text-white/80 mb-6 max-w-2xl mx-auto">
              {t.cta.text}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/booking">
                <Button className="bg-white text-[#2E5E99] hover:bg-white/90 px-8 py-3 h-auto text-base">
                  {t.cta.button}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/produktkatalog">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-3 h-auto text-base">
                  {t.cta.catalogLink}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default CaseStudyCyberSecurity;
